import { generateKeyBetween } from "fractional-indexing";

interface PositionedItem {
  readonly id: string;
  readonly position: string;
}

// Plain comparison instead of localeCompare: fractional indexing keys are ordered by their character codes.
function compare(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

/** Items in playlist order: by `position`, then by `id` for items that got the same position on two devices. */
export function sortByPosition<T extends PositionedItem>(
  items: readonly T[],
): T[] {
  return [...items].sort(
    (a, b) => compare(a.position, b.position) || compare(a.id, b.id),
  );
}

/**
 * The position for an item placed at `index` of `items`. `items` must be sorted and must not contain the placed item
 * itself (when moving, remove it first).
 */
export function positionAt(
  items: readonly PositionedItem[],
  index: number,
): string {
  const lower = index > 0 ? items[index - 1].position : null;
  // Skip items with the same position as the lower neighbour: there is no key between equal keys.
  const upper =
    items
      .slice(index)
      .map((i) => i.position)
      .find((p) => lower === null || p > lower) ?? null;
  return generateKeyBetween(lower, upper);
}
